import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Modules, useLoggedInUserPermissions } from '~/components/Auth';
import { WithPerspectives } from '~/components/MyWorkspace/ManagePerspectives';
import { TableWidgetComponentFactory } from '~/components/MyWorkspace/TableWidget';
import { RewardingTabs } from '~/data/tabs';
import { useWorkspaceSettings } from '~/scenes/MyWorkspace/hooks';
import { People } from '~/services/Http';
import { useGetTeamTimesheet } from '~/services/Http/hooks';

import { WidgetProps } from '../../data/widgets';

import { TableHeader } from './components/TableHeader';
import { TeamMemberExtraHoursBalance } from './components/TeamMemberExtraHoursBalance';

const TeamTimeManagementWidgetComponent = TableWidgetComponentFactory({
  TableRowComponent: TeamMemberExtraHoursBalance,
  getIdentifier: (item: People.TimesheetShortDto) => item.id,
  TableHeaderComponent: TableHeader,
  dataCy: 'team-time-management-widget',
});

export interface Props extends WidgetProps<WithPerspectives> {}

export const TeamTimeManagementWidget: React.FC<Props> = ({ title }) => {
  // TODO activeWorkspaceRole should be passed through as part of perspective
  const { activeWorkspaceRole } = useWorkspaceSettings();

  const { t } = useTranslation();

  const getTeamTimesheetReqParams = {
    employeeId: 'me',
    roles: [activeWorkspaceRole],
  };
  const {
    data: timesheet,
    loading,
    failedToLoad,
  } = useGetTeamTimesheet(getTeamTimesheetReqParams);

  const permissions = useLoggedInUserPermissions();
  const rewardingModuleEnabledTabs = permissions.enabledTabs[Modules.REWARDING];
  const sortedTimesheetArray = (timesheet: People.TimesheetShortDto[]) =>
    timesheet?.sort((a, b) => {
      const aAsDate = new Date(
        moment.duration(a.extraHoursBalance).asMilliseconds(),
      );
      const bAsDate = new Date(
        moment.duration(b.extraHoursBalance).asMilliseconds(),
      );
      if (aAsDate < bAsDate) {
        return 1;
      } else if (aAsDate > bAsDate) {
        return -1;
      }

      return 0;
    });

  return (
    <TeamTimeManagementWidgetComponent
      title={title || t('teamTimeManagement')}
      data={timesheet && sortedTimesheetArray(timesheet)}
      loading={loading}
      failedToLoad={failedToLoad}
      noDataMessage={t('noExtraHoursBalanceInfo')}
      isTabDisabled={
        !!rewardingModuleEnabledTabs &&
        !rewardingModuleEnabledTabs.includes(RewardingTabs.WORKTIME)
      }
    />
  );
};
